import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {BACKEND_URL} from '../../utils/loadEnv'
import './PaymentReminderPage.css'

type LoadState = 'idle' | 'loading' | 'loaded' | 'error'
type ModalView = 'list' | 'mail'

type ReminderHistory = {
    id: number
    sent_at: string
    sent_to: string
    subject: string
}

type ReminderCandidate = {
    id: number
    invoice_number: string
    order_id: string | null
    invoice_date: string
    invoice_date_iso: string
    age_days: number
    author: string
    recipient: string
    mailing_mail: string
    paid: boolean
    reminder_count: number
    last_reminder_sent_at: string | null
    reminders: ReminderHistory[]
}

type MailPreview = {
    invoice_id: number
    invoice_number: string
    recipient: string
    invoice_date: string
    to: string
    cc: string
    subject: string
    text: string
}

const extractResponse = (raw: unknown): { success: boolean; message: string; data: any; sent?: any[]; failed?: any[] } => {
    const body =
        raw && typeof raw === 'object' && 'body' in raw && (raw as any).body && typeof (raw as any).body === 'object'
            ? (raw as any).body
            : raw

    const b = body as any
    return {
        success: Boolean(b?.success),
        message: typeof b?.message === 'string' ? b.message : '',
        data: b?.data,
        sent: Array.isArray(b?.sent) ? b.sent : undefined,
        failed: Array.isArray(b?.failed) ? b.failed : undefined
    }
}

const normalizeCandidates = (items: unknown): ReminderCandidate[] => {
    if (!Array.isArray(items)) return []

    return items
        .filter((x) => x && typeof x === 'object')
        .map((x) => {
            const item = x as any
            const reminders = Array.isArray(item.reminders)
                ? item.reminders.map((r: any) => ({
                    id: Number(r?.id),
                    sent_at: String(r?.sent_at ?? ''),
                    sent_to: String(r?.sent_to ?? ''),
                    subject: String(r?.subject ?? '')
                })).filter((r: ReminderHistory) => Number.isFinite(r.id))
                : []

            return {
                id: Number(item.id),
                invoice_number: String(item.invoice_number ?? ''),
                order_id: item.order_id == null ? null : String(item.order_id),
                invoice_date: String(item.invoice_date ?? ''),
                invoice_date_iso: String(item.invoice_date_iso ?? ''),
                age_days: Number(item.age_days),
                author: String(item.author ?? ''),
                recipient: String(item.recipient ?? ''),
                mailing_mail: String(item.mailing_mail ?? ''),
                paid: Boolean(item.paid),
                reminder_count: Number(item.reminder_count ?? reminders.length),
                last_reminder_sent_at: item.last_reminder_sent_at == null ? null : String(item.last_reminder_sent_at),
                reminders
            }
        })
        .filter((x) => Number.isFinite(x.id) && x.id > 0 && Number.isFinite(x.age_days))
}

const normalizeMailPreview = (raw: unknown): MailPreview | null => {
    if (!raw || typeof raw !== 'object') return null
    const x = raw as any

    return {
        invoice_id: Number(x.invoice_id),
        invoice_number: String(x.invoice_number ?? ''),
        recipient: String(x.recipient ?? ''),
        invoice_date: String(x.invoice_date ?? ''),
        to: String(x.to ?? ''),
        cc: String(x.cc ?? ''),
        subject: String(x.subject ?? ''),
        text: String(x.text ?? '')
    }
}

const authorLabel = (author: string) => author.includes('Familienkost') ? 'Familienkost GmbH' : 'Verlag Terence Böhme'

const formatReminderSummary = (candidate: ReminderCandidate) => {
    if (candidate.reminder_count <= 0) return 'Keine Erinnerung'
    if (candidate.last_reminder_sent_at) {
        const lastDate = candidate.last_reminder_sent_at.split(' ')[0] ?? candidate.last_reminder_sent_at
        return `${candidate.reminder_count}x, zuletzt ${lastDate}`
    }
    return `${candidate.reminder_count}x erinnert`
}

const PaymentReminderPage: React.FC = () => {
    const navigate = useNavigate()
    const modalBodyRef = useRef<HTMLDivElement | null>(null)

    const [loadState, setLoadState] = useState<LoadState>('idle')
    const [error, setError] = useState('')
    const [candidates, setCandidates] = useState<ReminderCandidate[]>([])
    const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set())

    const [previewOpen, setPreviewOpen] = useState(false)
    const [modalView, setModalView] = useState<ModalView>('list')
    const [listScrollTop, setListScrollTop] = useState(0)
    const [mailPreview, setMailPreview] = useState<MailPreview | null>(null)
    const [mailLoading, setMailLoading] = useState(false)
    const [mailError, setMailError] = useState('')

    const [sendState, setSendState] = useState<LoadState>('idle')
    const [sendMessage, setSendMessage] = useState('')

    const loadCandidates = async (signal?: AbortSignal) => {
        if (!BACKEND_URL || BACKEND_URL.trim().length === 0) {
            setLoadState('error')
            setError('BACKEND_URL fehlt (REACT_APP_BACKEND_URL).')
            return
        }

        setLoadState('loading')
        setError('')

        try {
            const res = await fetch(`${BACKEND_URL}/invoices/payment-reminders/candidates`, {
                method: 'GET',
                credentials: 'include',
                signal,
                headers: {Accept: 'application/json'}
            })

            const raw = await res.json()
            const extracted = extractResponse(raw)
            if (!res.ok || !extracted.success) {
                throw new Error(extracted.message || `HTTP ${res.status}`)
            }

            const normalized = normalizeCandidates(extracted.data)
            setCandidates(normalized)
            setSelectedIds((prev) => {
                const allowed = new Set(normalized.map((x) => x.id))
                return new Set(Array.from(prev).filter((id) => allowed.has(id)))
            })
            setLoadState('loaded')
        } catch (err) {
            if ((err as any)?.name === 'AbortError') return
            setLoadState('error')
            setError((err as Error).message || 'Zahlungserinnerungen konnten nicht geladen werden')
        }
    }

    useEffect(() => {
        const controller = new AbortController()
        loadCandidates(controller.signal)
        return () => controller.abort()
    }, [])

    const sortedCandidates = useMemo(() => {
        return [...candidates].sort((a, b) => {
            if (a.age_days !== b.age_days) return b.age_days - a.age_days
            return b.id - a.id
        })
    }, [candidates])

    const selectedCandidates = useMemo(() => {
        const byId = new Map(sortedCandidates.map((candidate) => [candidate.id, candidate]))
        return Array.from(selectedIds)
            .map((id) => byId.get(id))
            .filter((x): x is ReminderCandidate => x != null)
    }, [selectedIds, sortedCandidates])

    const toggleSelected = (id: number) => {
        setSendMessage('')
        setSelectedIds((prev) => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    const selectOlderThan = (days: number) => {
        const ids = sortedCandidates
            .filter((candidate) => candidate.age_days > days && !candidate.paid)
            .map((candidate) => candidate.id)
        setSelectedIds(new Set(ids))
        setSendMessage(`${ids.length} Rechnung${ids.length === 1 ? '' : 'en'} ausgewählt.`)
    }

    const clearSelection = () => {
        setSelectedIds(new Set())
        setSendMessage('')
    }

    const openPreview = () => {
        if (selectedCandidates.length === 0) {
            setSendMessage('Bitte mindestens eine Rechnung auswählen.')
            return
        }
        setModalView('list')
        setMailPreview(null)
        setMailError('')
        setPreviewOpen(true)
    }

    const closePreview = () => {
        setPreviewOpen(false)
        setModalView('list')
        setMailPreview(null)
        setMailError('')
        setSendState('idle')
    }

    const openPdf = (invoiceId: number) => {
        window.open(`${BACKEND_URL}/invoices/payment-reminders/${invoiceId}/pdf`, '_blank', 'noopener,noreferrer')
    }

    const openMailPreview = async (candidate: ReminderCandidate) => {
        setListScrollTop(modalBodyRef.current?.scrollTop ?? 0)
        setModalView('mail')
        setMailPreview(null)
        setMailError('')
        setMailLoading(true)

        try {
            const res = await fetch(`${BACKEND_URL}/invoices/payment-reminders/${candidate.id}/mail-preview`, {
                method: 'GET',
                credentials: 'include',
                headers: {Accept: 'application/json'}
            })
            const raw = await res.json()
            const extracted = extractResponse(raw)
            if (!res.ok || !extracted.success) {
                throw new Error(extracted.message || `HTTP ${res.status}`)
            }

            const preview = normalizeMailPreview(extracted.data)
            if (!preview) throw new Error('Mailvorschau konnte nicht gelesen werden.')
            setMailPreview(preview)
        } catch (err) {
            setMailError((err as Error).message || 'Mailvorschau konnte nicht geladen werden.')
        } finally {
            setMailLoading(false)
        }
    }

    const backToPreviewList = () => {
        setModalView('list')
        setMailPreview(null)
        setMailError('')
        window.setTimeout(() => {
            if (modalBodyRef.current) modalBodyRef.current.scrollTop = listScrollTop
        }, 0)
    }

    const sendSelected = async () => {
        if (selectedCandidates.length === 0) return

        setSendState('loading')
        setSendMessage('')

        try {
            const res = await fetch(`${BACKEND_URL}/invoices/payment-reminders/send`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({invoice_ids: selectedCandidates.map((candidate) => candidate.id)})
            })

            const raw = await res.json()
            const extracted = extractResponse(raw)
            if (!res.ok) throw new Error(extracted.message || `HTTP ${res.status}`)

            const sentCount = extracted.sent?.length ?? 0
            const failedCount = extracted.failed?.length ?? 0
            setSendState(extracted.success ? 'loaded' : 'error')
            setSendMessage(
                failedCount === 0
                    ? `${sentCount} Zahlungserinnerung${sentCount === 1 ? '' : 'en'} versendet.`
                    : `${sentCount} versendet, ${failedCount} fehlgeschlagen.`
            )

            await loadCandidates()
            if (sentCount > 0) {
                setSelectedIds((prev) => {
                    const sentIds = new Set((extracted.sent ?? []).map((x: any) => Number(x.invoice_id)))
                    return new Set(Array.from(prev).filter((id) => !sentIds.has(id)))
                })
            }
            if (failedCount === 0) {
                setSelectedIds(new Set())
                setPreviewOpen(false)
                setModalView('list')
            }
        } catch (err) {
            setSendState('error')
            setSendMessage((err as Error).message || 'Zahlungserinnerungen konnten nicht versendet werden.')
        }
    }

    return (
        <main className='paymentReminderPage'>
            <div className='paymentReminderCard'>
                <header className='paymentReminderHeader'>
                    <div>
                        <button
                            type='button'
                            className='paymentReminderBack'
                            onClick={() => navigate('/')}
                        >
                            Zurück
                        </button>
                        <h1>Zahlungserinnerung versenden</h1>
                    </div>

                    <img
                        className='paymentReminderLogo'
                        src='https://www.familienkost.de/icon-svgs/logos/Familienkost_Logo_Horizontal_Farbig.svg'
                        alt='Familienkost Logo'
                    />
                </header>

                <section className='paymentReminderToolbar'>
                    <div className='paymentReminderQuickSelect'>
                        <button type='button' onClick={() => selectOlderThan(90)}>Über 90 Tage</button>
                        <button type='button' onClick={() => selectOlderThan(120)}>Über 120 Tage</button>
                        <button type='button' onClick={() => selectOlderThan(180)}>Über 180 Tage</button>
                        <button type='button' onClick={clearSelection}>Auswahl leeren</button>
                    </div>

                    <div className='paymentReminderActionRow'>
                        <div className='paymentReminderCount'>
                            {selectedCandidates.length} von {sortedCandidates.length} ausgewählt
                        </div>
                        <button
                            type='button'
                            className='paymentReminderPrimary'
                            onClick={openPreview}
                            disabled={selectedCandidates.length === 0}
                        >
                            Zahlungserinnerung senden
                        </button>
                    </div>
                </section>

                {sendMessage && !previewOpen && (
                    <div className={sendState === 'error' ? 'paymentReminderError' : 'paymentReminderHint'}>
                        {sendMessage}
                    </div>
                )}

                {loadState === 'loading' && (
                    <div className='paymentReminderHint'>Rechnungen werden geladen…</div>
                )}

                {loadState === 'error' && (
                    <div className='paymentReminderError'>{error}</div>
                )}

                {loadState === 'loaded' && sortedCandidates.length === 0 && (
                    <div className='paymentReminderEmpty'>
                        Keine unbezahlten Rechnungen mit mehr als 30 Tagen seit Datum gefunden.
                    </div>
                )}

                {loadState === 'loaded' && sortedCandidates.length > 0 && (
                    <div className='paymentReminderTableWrap'>
                        <table className='paymentReminderTable'>
                            <thead>
                            <tr>
                                <th>Rechnung</th>
                                <th>Order-ID</th>
                                <th>Datum</th>
                                <th>Autor</th>
                                <th>Empfänger</th>
                                <th>Senden</th>
                            </tr>
                            </thead>
                            <tbody>
                            {sortedCandidates.map((candidate) => (
                                <tr key={candidate.id}>
                                    <td>
                                        <span className='paymentReminderInvoice'>
                                            {candidate.invoice_number}
                                        </span>
                                        <span className='paymentReminderAge'>
                                            {candidate.age_days} Tage
                                        </span>
                                    </td>
                                    <td>{candidate.order_id ?? '—'}</td>
                                    <td>{candidate.invoice_date}</td>
                                    <td>{authorLabel(candidate.author)}</td>
                                    <td title={candidate.recipient}>{candidate.recipient}</td>
                                    <td>
                                        <label className='paymentReminderSendCell'>
                                            <input
                                                type='checkbox'
                                                checked={selectedIds.has(candidate.id)}
                                                onChange={() => toggleSelected(candidate.id)}
                                            />
                                            <span>{formatReminderSummary(candidate)}</span>
                                        </label>
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {previewOpen && (
                <div className='paymentReminderOverlay' role='dialog' aria-modal='true'>
                    <div className='paymentReminderModal'>
                        <div className='paymentReminderModalHeader'>
                            <div>
                                <h2>{modalView === 'mail' ? 'Mailvorschau' : 'Vorschau prüfen'}</h2>
                                <p>{selectedCandidates.length} Zahlungserinnerung{selectedCandidates.length === 1 ? '' : 'en'} ausgewählt</p>
                            </div>

                            <button type='button' className='paymentReminderClose' onClick={closePreview}>
                                ×
                            </button>
                        </div>

                        <div className='paymentReminderModalBody' ref={modalBodyRef}>
                            {modalView === 'list' && (
                                <div className='paymentReminderPreviewGrid'>
                                    {selectedCandidates.map((candidate) => (
                                        <article className='paymentReminderPreviewCard' key={candidate.id}>
                                            <div className='paymentReminderPreviewTitle'>
                                                <span>{candidate.invoice_number}</span>
                                                <span>{candidate.age_days} Tage offen</span>
                                            </div>

                                            <dl className='paymentReminderPreviewMeta'>
                                                <div>
                                                    <dt>Empfänger</dt>
                                                    <dd>{candidate.recipient}</dd>
                                                </div>
                                                <div>
                                                    <dt>Erstellungsdatum</dt>
                                                    <dd>{candidate.invoice_date}</dd>
                                                </div>
                                                <div>
                                                    <dt>Mail an</dt>
                                                    <dd>{candidate.mailing_mail}</dd>
                                                </div>
                                            </dl>

                                            <div className='paymentReminderHistory'>
                                                {candidate.reminders.length === 0 ? (
                                                    <span>Noch keine Erinnerung versendet</span>
                                                ) : (
                                                    candidate.reminders.map((reminder) => (
                                                        <span key={reminder.id}>
                                                            Erinnerung am {reminder.sent_at} an {reminder.sent_to}
                                                        </span>
                                                    ))
                                                )}
                                            </div>

                                            <div className='paymentReminderPreviewActions'>
                                                <button type='button' onClick={() => openPdf(candidate.id)}>
                                                    PDF öffnen
                                                </button>
                                                <button type='button' onClick={() => openMailPreview(candidate)}>
                                                    Mail ansehen
                                                </button>
                                            </div>
                                        </article>
                                    ))}
                                </div>
                            )}

                            {modalView === 'mail' && (
                                <div className='paymentReminderMailView'>
                                    <button
                                        type='button'
                                        className='paymentReminderBack'
                                        onClick={backToPreviewList}
                                    >
                                        Zurück zur Vorschau
                                    </button>

                                    {mailLoading && (
                                        <div className='paymentReminderHint'>Mailvorschau wird geladen…</div>
                                    )}

                                    {mailError && (
                                        <div className='paymentReminderError'>{mailError}</div>
                                    )}

                                    {mailPreview && (
                                        <>
                                            <div className='paymentReminderMailMeta'>
                                                <div><strong>An:</strong> {mailPreview.to}</div>
                                                <div><strong>CC:</strong> {mailPreview.cc}</div>
                                                <div><strong>Betreff:</strong> {mailPreview.subject}</div>
                                            </div>

                                            <article className='paymentReminderMailPaper'>
                                                <img
                                                    src='https://www.familienkost.de/icon-svgs/logos/Familienkost_Logo_Horizontal_Farbig.svg'
                                                    alt='Familienkost Logo'
                                                />

                                                <pre>{mailPreview.text}</pre>
                                            </article>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className='paymentReminderModalFooter'>
                            {sendMessage && (
                                <div className={sendState === 'error' ? 'paymentReminderErrorInline' : 'paymentReminderHintInline'}>
                                    {sendMessage}
                                </div>
                            )}

                            <button type='button' className='paymentReminderSecondary' onClick={closePreview}>
                                Abbrechen
                            </button>
                            <button
                                type='button'
                                className='paymentReminderPrimary'
                                onClick={sendSelected}
                                disabled={sendState === 'loading' || selectedCandidates.length === 0}
                            >
                                {sendState === 'loading' ? 'Wird gesendet…' : 'Ausgewählte senden'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    )
}

export default PaymentReminderPage
